import { createApi } from "@reduxjs/toolkit/query/react";
import { prepareBaseQuery } from "./baseQueryWithReauth";

// Enable and rotate answer with the same payload as the status route (token,
// key, guestUrl, enabled, expiresAt). Write it straight into the cached status
// so the admin page shows it without a second request.
async function mergeIntoAccessStatus(eventId, dispatch, queryFulfilled) {
  try {
    const { data } = await queryFulfilled;
    if (!eventId || !data) return;
    dispatch(
      songRequestsApi.util.updateQueryData(
        "getSongRequestsAccessStatus",
        eventId,
        (draft) => ({ ...(draft ?? {}), ...data }),
      ),
    );
  } catch {
    // The calling component reports the error.
  }
}

// One random id per browser, sent as X-Guest-Device on the guest list and
// submit calls, so the backend limits one phone instead of a whole venue
// Wi-Fi. Not an identity: 16 random bytes, base64url, nothing read from the
// device. Kept in localStorage; in memory only when storage is blocked.
const GUEST_DEVICE_STORAGE_KEY = "djr.songRequests.deviceId.v1";
const GUEST_DEVICE_RE = /^[A-Za-z0-9_-]{16,64}$/;
let memoryGuestDeviceId = "";

function newGuestDeviceId() {
  const cryptoApi = typeof window !== "undefined" ? window.crypto : undefined;
  if (typeof cryptoApi?.getRandomValues !== "function") return "";
  const bytes = new Uint8Array(16);
  cryptoApi.getRandomValues(bytes);
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function getGuestDeviceId() {
  if (memoryGuestDeviceId) return memoryGuestDeviceId;
  try {
    const stored = window.localStorage.getItem(GUEST_DEVICE_STORAGE_KEY);
    if (typeof stored === "string" && GUEST_DEVICE_RE.test(stored)) {
      memoryGuestDeviceId = stored;
      return stored;
    }
  } catch {
    // Storage blocked: fall through to a fresh id kept in memory.
  }
  const fresh = newGuestDeviceId();
  if (!fresh) return "";
  memoryGuestDeviceId = fresh;
  try {
    window.localStorage.setItem(GUEST_DEVICE_STORAGE_KEY, fresh);
  } catch {
    // Storage blocked: the in-memory id lasts for this page load.
  }
  return fresh;
}

// Without an id (no Web Crypto) the header is left out and the backend
// counts the address instead.
function guestDeviceHeaders() {
  const id = getGuestDeviceId();
  return id ? { "X-Guest-Device": id } : {};
}

const songRequestsApi = createApi({
  reducerPath: "songRequests",
  baseQuery: prepareBaseQuery(""),
  tagTypes: ["SongRequest", "PublicEvent", "SongRequestsAccess"],
  endpoints: (builder) => ({
    // Public: safe event metadata (requires a valid access key)
    getPublicEvent: builder.query({
      query: ({ eventId, accessKey }) =>
        `/events/public/${eventId}?k=${encodeURIComponent(accessKey)}`,
      providesTags: (result, error, { eventId }) => [
        { type: "PublicEvent", id: eventId },
      ],
    }),

    // Public: list requests (requires a valid access key)
    listSongRequestsPublic: builder.query({
      query: ({ eventId, accessKey }) => ({
        url: `/song-requests/event/${eventId}?k=${encodeURIComponent(accessKey)}`,
        headers: guestDeviceHeaders(),
      }),
      providesTags: (result, error, { eventId }) => [
        { type: "SongRequest", id: eventId },
      ],
    }),

    // Public: create request (requires a valid access key). A 429 carries
    // retryAfterSeconds (djr-be middleware/songRequestsRateLimit.js).
    // `track` (the Spotify pick from SongSearchField) travels only when the
    // guest tapped a result; free text sends none (task 2026-09-26).
    createSongRequest: builder.mutation({
      query: ({ eventId, accessKey, songText, requesterName, track }) => ({
        url: `/song-requests/event/${eventId}?k=${encodeURIComponent(accessKey)}`,
        method: "POST",
        body: track ? { songText, requesterName, track } : { songText, requesterName },
        headers: guestDeviceHeaders(),
      }),
      invalidatesTags: (r, e, { eventId }) => [
        { type: "SongRequest", id: eventId },
      ],
    }),

    // Public: the Spotify typeahead behind the song box. Results only, never
    // anything from Andrew's library. { results, unavailable }. A 429 means
    // the search limit; the field falls back to free text either way.
    searchSongs: builder.query({
      query: ({ eventId, accessKey, q }) => ({
        url: `/song-requests/event/${eventId}/search?k=${encodeURIComponent(accessKey)}&q=${encodeURIComponent(q)}`,
        headers: guestDeviceHeaders(),
      }),
      keepUnusedDataFor: 300,
    }),

    // Public: which event is live right now, for the printed sign at
    // /requests. { status: "one", eventId, guestUrl } | { status: "none" } |
    // { status: "many", events: [{ eventId, title, guestUrl }] }. Never cached.
    getLiveSongRequestsEvent: builder.query({
      query: () => "/song-requests/live",
      keepUnusedDataFor: 0,
    }),

    // Admin: list requests (can be used even if event is not public)
    listSongRequestsAdmin: builder.query({
      query: (eventId) => `/song-requests/event/${eventId}/admin`,
      providesTags: (result, error, eventId) => [
        { type: "SongRequest", id: eventId },
      ],
    }),

    // Admin: access status. The server derives the guest token on every call
    // and returns it as `token` (and as the legacy `key`) plus the full
    // `guestUrl`, so the admin can always show the current link and QR.
    // Read-only: loading it never enables or rotates anything.
    getSongRequestsAccessStatus: builder.query({
      query: (eventId) => `/events/${eventId}/song-requests/access`,
      providesTags: (result, error, eventId) => [
        { type: "SongRequestsAccess", id: eventId },
      ],
    }),

    // Admin: enable. Idempotent on the server: while the link is live it
    // writes nothing and returns the same token. ttlHours is sent only when
    // the admin picked a custom expiry; otherwise the server uses the event
    // based default.
    enableSongRequestsAccess: builder.mutation({
      query: ({ eventId, ttlHours }) => ({
        url: `/events/${eventId}/song-requests/access/enable`,
        method: "POST",
        body: typeof ttlHours === "number" ? { ttlHours } : {},
      }),
      async onQueryStarted({ eventId }, { dispatch, queryFulfilled }) {
        await mergeIntoAccessStatus(eventId, dispatch, queryFulfilled);
      },
    }),

    // Admin: rotate. Destructive: tokenVersion += 1 on the server, so every
    // printed or shared QR and link for this event stops working.
    rotateSongRequestsAccess: builder.mutation({
      query: (eventId) => ({
        url: `/events/${eventId}/song-requests/access/rotate`,
        method: "POST",
        body: {},
      }),
      async onQueryStarted(eventId, { dispatch, queryFulfilled }) {
        await mergeIntoAccessStatus(eventId, dispatch, queryFulfilled);
      },
    }),

    // Admin: disable
    disableSongRequestsAccess: builder.mutation({
      query: (eventId) => ({
        url: `/events/${eventId}/song-requests/access/disable`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: (r, e, eventId) =>
        eventId ? [{ type: "SongRequestsAccess", id: eventId }] : [],
    }),

    // Admin: update status
    updateSongRequestStatus: builder.mutation({
      query: ({ id, status, eventId }) => ({
        url: `/song-requests/${id}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (r, e, { eventId }) =>
        eventId ? [{ type: "SongRequest", id: eventId }] : [],
    }),

    // Admin: delete
    deleteSongRequest: builder.mutation({
      query: ({ id, eventId }) => ({
        url: `/song-requests/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (r, e, { eventId }) =>
        eventId ? [{ type: "SongRequest", id: eventId }] : [],
    }),
  }),
});

export const {
  useGetPublicEventQuery,
  useListSongRequestsPublicQuery,
  useCreateSongRequestMutation,
  useLazySearchSongsQuery,
  useGetLiveSongRequestsEventQuery,
  useListSongRequestsAdminQuery,
  useUpdateSongRequestStatusMutation,
  useDeleteSongRequestMutation,
  useGetSongRequestsAccessStatusQuery,
  useEnableSongRequestsAccessMutation,
  useRotateSongRequestsAccessMutation,
  useDisableSongRequestsAccessMutation,
} = songRequestsApi;

export default songRequestsApi;
export { songRequestsApi };
